import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Mail, ArrowLeft, AlertCircle } from 'lucide-react';
import useAuthStore from '../../store/authStore';

const forgotPasswordSchema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
});

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const { forgotPassword, isLoading, error } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(forgotPasswordSchema),
  });

  const onSubmit = async (data) => {
    try {
      await forgotPassword(data.email);
      // Navigate to reset password page with email in state
      navigate('/reset-password', { state: { email: data.email } });
    } catch (error) {
      console.error('Forgot password error:', error);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center mb-2">
          <div className="w-1 h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-2" />
          <h2 className="text-xl font-bold text-gray-800">Forgot your password?</h2>
        </div>
        <p className="text-sm text-gray-500 ml-3">
          Enter your email address and we'll send you a verification code to reset your password.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 rounded-xl p-4 shadow-neomorph-inset border border-red-200/50">
          <div className="flex items-start">
            <div className="p-1.5 bg-red-100 rounded-md shadow-neomorph-inset mr-3">
              <AlertCircle className="h-4 w-4 text-red-600" />
            </div>
            <div>
              <h3 className="text-xs font-medium text-red-800">Error</h3>
              <p className="text-xs text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <div>
          <label htmlFor="email" className="block text-xs font-medium text-gray-600 mb-1.5">
            Email address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-4 w-4 text-gray-400" />
            </div>
            <input
              id="email"
              type="email"
              autoComplete="email"
              {...register('email')}
              className={`block w-full pl-9 pr-3 py-2.5 bg-[#E8ECF5] border-0 rounded-lg shadow-neomorph-inset text-gray-800 text-sm focus:outline-none focus:ring-2 ${
                errors.email ? 'focus:ring-red-500' : 'focus:ring-primary-500/50'
              }`}
              placeholder="you@example.com"
            />
          </div>
          {errors.email && (
            <p className="mt-1.5 text-xs text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-4 py-3 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <div className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Sending...
              </div>
            ) : (
              'Send verification code'
            )}
          </button>
        </div>
      </form>

      <div className="text-center">
        <Link
          to="/login"
          className="inline-flex items-center text-xs font-medium text-primary-600 hover:text-primary-700 bg-primary-50 rounded-lg px-3 py-1.5 shadow-neomorph-inset"
        >
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" />
          Back to login
        </Link>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;